import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/* ===========================================================================
   SIMONE - PINNED CHECKOUT, SLIM FOOTER (dialed)

   Every pixel reclaimed comes out of the FOOTER. Product rows stay full size.

       checkout button   60px -> 42px
       Shop Pay row      52px -> 38px   (Shopify's own size variable)
       subtotal rows     48px -> 32px
       card logo strip   54px -> 0      (decoration only)
       ------------------------------------
       footer           286px -> ~196px

       product image    96px   (stock is 100px)
       title            15px   (stock)

   If the footer ever needs to come back up, raise these four values:
     .sm-checkout padding/font
     --shopify-accelerated-checkout-button-block-size
     .shopify-payment-button__button height
     .sm-summary__row font-size
   Do NOT shrink .sm-line__img again. That was the wrong lever.
   =========================================================================== */
(() => {
  const CSS = `
/* column layout: header, scrolling items, locked footer */
.cart-drawer__dialog .cart-drawer__inner{display:flex!important;flex-direction:column!important;max-height:100dvh!important}
.cart-drawer__dialog .cart-drawer__content{flex:1 1 auto!important;min-height:0!important;overflow-y:auto!important}
.cart-drawer__dialog .cart-drawer__summary{flex:0 0 auto!important;position:sticky!important;bottom:0!important;z-index:6!important;background:#fff!important;padding-top:0!important;padding-bottom:5px!important;box-shadow:0 -8px 20px rgba(0,0,0,.07)!important}

/* line items: full size, only dead vertical padding trimmed */
.cart-drawer__dialog .sm-line{grid-template-columns:96px minmax(0,1fr)!important;column-gap:14px!important;padding:11px 0!important}
.cart-drawer__dialog .sm-line--free{grid-template-columns:96px minmax(0,1fr)!important;column-gap:14px!important;padding:12px 12px!important;margin:10px 0 4px!important}
.cart-drawer__dialog .sm-line__media,.cart-drawer__dialog .sm-line--free .sm-line__media{width:96px!important;height:96px!important}
.cart-drawer__dialog .sm-line__img{width:96px!important;height:96px!important}
.cart-drawer__dialog .sm-line__title{font-size:15px!important;line-height:1.32!important;margin:0 0 5px!important}
.cart-drawer__dialog .sm-line__controls{margin-top:6px!important}

/* ---- footer: all reclaimed space comes from here ---- */
.cart-drawer__dialog .sm-summary{padding-top:7px!important}
.cart-drawer__dialog .sm-summary .cart__total-container{gap:2px!important}
.cart-drawer__dialog .sm-summary__row{font-size:13px!important;line-height:1.3!important}
.cart-drawer__dialog .sm-checkout{min-height:0!important;height:auto!important;padding:10px 16px!important;font-size:14.5px!important;line-height:1.25!important}

/* Shop Pay / PayPal / Apple Pay row, sized through Shopify's own custom
   property so the buttons stay sharp instead of squashed by a transform. */
.cart-drawer__dialog .sm-express{margin-top:6px!important}
.cart-drawer__dialog .sm-express,
.cart-drawer__dialog shopify-accelerated-checkout,
.cart-drawer__dialog shopify-accelerated-checkout-cart{
  --shopify-accelerated-checkout-button-block-size:38px!important;
  --shopify-accelerated-checkout-button-inline-size:100%!important;
  --shopify-accelerated-checkout-row-gap:5px!important;
}
.cart-drawer__dialog .shopify-payment-button__button{min-height:38px!important;height:38px!important;padding:0 14px!important;font-size:14px!important}

/* card logo strip is decoration and costs ~54px above the fold.
   Delete the next line if you want the icons back. */
.cart-drawer__dialog .sm-pay{display:none!important}

/* ---- scroll cue ---- */
.cart-drawer__dialog{position:relative}
#sm-scroll-cue{
  position:absolute;left:50%;bottom:0;transform:translate(-50%,0) scale(.85);
  width:36px;height:36px;padding:0;border:1px solid rgba(0,0,0,.07);border-radius:50%;
  background:rgba(255,255,255,.94);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);
  box-shadow:0 4px 14px rgba(0,0,0,.16);color:#1a1a1a;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  opacity:0;pointer-events:none;z-index:7;
  transition:opacity .22s ease,transform .22s ease;
}
#sm-scroll-cue.is-on{opacity:1;pointer-events:auto;transform:translate(-50%,0) scale(1);animation:smCueBob 1.9s ease-in-out infinite}
#sm-scroll-cue:active{transform:translate(-50%,0) scale(.92);animation:none}
#sm-scroll-cue svg{width:17px;height:17px;display:block}
@keyframes smCueBob{0%,100%{margin-bottom:0}50%{margin-bottom:4px}}
@media(prefers-reduced-motion:reduce){#sm-scroll-cue.is-on{animation:none}}

@media(max-width:380px){
  .cart-drawer__dialog .sm-line,.cart-drawer__dialog .sm-line--free{grid-template-columns:88px minmax(0,1fr)!important}
  .cart-drawer__dialog .sm-line__media,.cart-drawer__dialog .sm-line--free .sm-line__media,
  .cart-drawer__dialog .sm-line__img{width:88px!important;height:88px!important}
}`;
  const el = document.createElement('style');
  el.id = 'sm-cart-css';
  el.textContent = CSS;
  (document.head || document.documentElement).appendChild(el);
})();

/* ===========================================================================
   SIMONE - SCROLL CUE
   A floating arrow that appears only when there are items below the fold and
   scrolls one screen per tap. It hides itself at the bottom and when the list
   is short enough to fit, so a one-item cart never shows it.

   Horizon replaces the cart list HTML on every cart:update, so the cue is
   appended to the DIALOG (which survives) and the scroll listener is re-wired
   whenever the list element is swapped.
   =========================================================================== */
(() => {
  const ID = 'sm-scroll-cue';
  const EDGE = 24;    // px of remaining scroll before the list counts as "at the end"
  const STEP = 0.82;  // fraction of the visible list height moved per tap
  const ARROW =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';

  function makeCue(dialog) {
    let cue = dialog.querySelector('#' + ID);
    if (cue) return cue;
    cue = document.createElement('button');
    cue.id = ID;
    cue.type = 'button';
    cue.setAttribute('aria-label', 'Scroll down for more items');
    cue.innerHTML = ARROW;
    dialog.appendChild(cue);
    return cue;
  }

  function sync(dialog) {
    const list = dialog.querySelector('.cart-drawer__content');
    const cue = dialog.querySelector('#' + ID);
    if (!list || !cue) return;

    // park the cue just above the pinned footer
    const footer = dialog.querySelector('.cart-drawer__summary');
    const fh = footer ? footer.getBoundingClientRect().height : 0;
    cue.style.bottom = fh + 12 + 'px';

    const left = list.scrollHeight - list.scrollTop - list.clientHeight;
    cue.classList.toggle('is-on', left > EDGE);
  }

  function wire(dialog) {
    const list = dialog.querySelector('.cart-drawer__content');
    if (!list) return;

    const cue = makeCue(dialog);

    if (!cue.dataset.smBound) {
      cue.dataset.smBound = '1';
      cue.addEventListener('click', () => {
        const target = dialog.querySelector('.cart-drawer__content');
        if (!target) return;
        target.scrollBy({ top: Math.max(120, target.clientHeight * STEP), behavior: 'smooth' });
      });
    }

    // the list element itself is replaced on cart updates, so bind per element
    if (!list.dataset.smBound) {
      list.dataset.smBound = '1';
      list.addEventListener('scroll', () => sync(dialog), { passive: true });
    }

    sync(dialog);
  }

  function run() {
    document.querySelectorAll('.cart-drawer__dialog').forEach(wire);
  }

  // initial pass plus a short retry, since the drawer markup can land late
  run();
  setTimeout(run, 400);
  setTimeout(run, 1200);

  document.addEventListener('cart:update', () => setTimeout(run, 140));
  document.addEventListener('DialogOpen', () => setTimeout(run, 60), true);
  window.addEventListener('resize', () => run(), { passive: true });

  // Horizon swaps the list HTML in place; catch that without polling
  const host = document.querySelector('cart-drawer-component') || document.body;
  let t = null;
  new MutationObserver(() => {
    clearTimeout(t);
    t = setTimeout(run, 90);
  }).observe(host, { childList: true, subtree: true });
})();

/* ===========================================================================
   SIMONE - BUNDLE GUARD
   Pulls the gifts out of the cart when the bundle that earned them is gone,
   so a free bottle never sits there on its own after a paid one is removed.

   A gift survives only while 2 or more paid bundle bottles remain:
     2+ paid -> keep the free bottles, butter and parfum
     1  paid -> that is the Buy 1 tier, which earns no gifts
     0  paid -> nothing left to earn them

   Scope: only touches lines the 3-tier PDP tagged with `_bundle`. The B1G1
   page does not set it, so its free bottle is never touched, even though both
   pages add the SAME free-bottle variants.
   =========================================================================== */
(() => {
  const GIFT = new Set([
    45971585269862, 45971585171558, 45971585204326, 45971585237094,
    47202347876454, 47202348138598,
  ]);
  const MIN_PAID = 2;
  const SRC = 'sm-bundle-guard';
  let busy = false;

  const mine = (l) => !!(l.properties && l.properties._bundle);
  const gift = (l) => GIFT.has(l.variant_id);

  async function check() {
    // __plmBuilding is set while the PDP is mid-build. Acting now would delete
    // gifts before the paid bottles it is still adding have landed.
    if (busy || window.__plmBuilding) return;
    busy = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const items = cart.items || [];

      let paid = 0;
           for (const l of items) if (mine(l) && !gift(l)) paid += l.quantity * (l.handle === 'desire-oil-set' ? 2 : 1);
      if (paid >= MIN_PAID) return;

      const updates = {};
      let n = 0;
      for (const l of items) if (mine(l) && gift(l)) { updates[l.variant_id] = 0; n++; }
      if (!n) return;

      const next = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      }).then((r) => r.json());

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: next, sourceId: SRC, data: { source: SRC, itemCount: next && next.item_count } },
      }));
    } catch (e) {
      console.error('[simone] bundle guard', e);
    } finally {
      busy = false;
    }
  }

  document.addEventListener('cart:update', (e) => {
    if (e?.detail?.sourceId === SRC) return;
    setTimeout(check, 60);
  });
  setTimeout(check, 800);
})();